import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, finalize, tap } from 'rxjs/operators';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { ToastrService } from 'ngx-toastr';
import { CompanyOnboardingService } from '../services/company-onboarding.service';
import { RecruitmentJobService } from '@edtech-ui/core/services/recruitment-management/recruitment-job.service';

// Subcomponents
import { RecruiterSidebarComponent } from './components/recruiter-sidebar/recruiter-sidebar.component';
import { RecruiterHeaderComponent } from './components/recruiter-header/recruiter-header.component';

@Component({
  selector: 'app-recruiter-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RecruiterSidebarComponent,
    RecruiterHeaderComponent
  ],
  templateUrl: './recruiter-dashboard.component.html',
  styleUrls: ['./recruiter-dashboard.component.scss']
})
export class RecruiterDashboardComponent implements OnInit, OnDestroy {
  isSidebarCollapsed = false;
  isMobileMenuOpen = false;
  activeTab = 'dashboard';
  globalSearchQuery = '';
  showPendingModal = false;
  registeredEmail = '';
  isAuthenticated = false;

  private routerSub!: Subscription;

  constructor(
    private router: Router,
    private _authService: AuthServiceService,
    private _toastr: ToastrService,
    private onboardingService: CompanyOnboardingService,
    private jobService: RecruitmentJobService
  ) {
    // Listen to routing changes and auto-update sidebar active highlights
    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateActiveTabFromRoute();
      if (!this.router.url.includes('/recruiter-dashboard/jobs')) {
        this.jobService.lastJobsPage = 1;
      }
    });
  }

  get effectiveSidebarCollapsed(): boolean {
    if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
      return false;
    }
    return this.isSidebarCollapsed;
  }

  @HostListener('window:resize')
  onResize(): void {
    if (typeof window !== 'undefined' && window.innerWidth > 1024) {
      this.isMobileMenuOpen = false;
    }
  }

  ngOnInit(): void {
    this.updateActiveTabFromRoute();
    const token = localStorage.getItem('token');
    const companyId = localStorage.getItem('company_id');
    this.isAuthenticated = !!(token && companyId);

    // Check company status and redirect if not approved
    if (this.isAuthenticated) {
      this.onboardingService.getCompanyProfile().subscribe({
        next: (profileRes: any) => {
          if (profileRes && profileRes.Success && profileRes.Data) {
            const status = profileRes.Data.status;
            if (status === 'pending' || status === 'rejected' || status === 'need_clarification' || status === 'clarification') {
              this.router.navigate(['/company-status']);
            }
          }
        },
        error: (err: any) => {
          console.error('Error fetching company profile on dashboard init', err);
        }
      });
    }

    // // Check if the user is in pending state
    // if (localStorage.getItem('companyStatus') === 'pending') {
    //   this.showPendingModal = true;
    //   this.registeredEmail = localStorage.getItem('registeredEmail') || '';
    // }
  }

  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  private updateActiveTabFromRoute(): void {
    const url = this.router.url;
    if (url.includes('/recruiter-dashboard/dashboard')) {
      this.activeTab = 'dashboard';
    } else if (url.includes('/recruiter-dashboard/jobs')) {
      this.activeTab = 'jobs';
    } else if (url.includes('/recruiter-dashboard/candidates')) {
      this.activeTab = 'candidates';
    } else if (url.includes('/recruiter-dashboard/assessments')) {
      this.activeTab = 'assessments';
    } else if (url.includes('/recruiter-dashboard/interview')) {
      this.activeTab = 'interview';
    } else if (url.includes('/recruiter-dashboard/one_one_interview')) {
      this.activeTab = 'one_one_interview';
    } else if (url.includes('/recruiter-dashboard/company-profile')) {
      this.activeTab = 'company-profile';
    } else if (url.includes('/company-onBoard')) {
      this.activeTab = '';
    }
  }

  get isCompanyOnboardPage(): boolean {
    return this.router.url.includes('/company-onBoard');
  }

  toggleSidebar(): void {
    if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
      this.isMobileMenuOpen = false;
    } else {
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
    }
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  setActiveTab(tab: string): void {
    if (tab !== 'jobs') {
      this.jobService.lastJobsPage = 1;
    }
    this.activeTab = tab;
    this.isMobileMenuOpen = false;

    // Navigate to respective nested child route, fallback to dashboard for standard empty tabs
    if (['dashboard', 'jobs', 'candidates', 'assessments', 'company-profile'].includes(tab)) {
      this.router.navigate([`/recruiter-dashboard/${tab}`]);
    } else {
      // For interview/1:1 fallback pages, we navigate back to parent route path
      this.router.navigate([`/recruiter-dashboard`]);
    }
  }

  logout(): void {
    this.routerSub.add(
      this._authService.logout(this.router).subscribe({
        next: (response) => {
          if (response.Success) {
            this._toastr.success('Logged out successfully.');
          } else {
            this._toastr.error(response.Message || 'Logout failed');
          }
        },
        error: () => {
          this._toastr.error('An error occurred during logout');
        }
      })
    );
  }

  onGlobalSearch(query: string): void {
    this.globalSearchQuery = query;
  }
}
